import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Box, Button, Typography, LinearProgress, Container, Fade, IconButton, TextField, MenuItem } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Close, CheckCircle, Cancel } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";

import { RegisterStepOne, RegisterStepTwo, SpecialtyAttributeStep, ExitConfirmationDialog } from "./components";
import {
    useUpdateDoctorMutation,
    useUpdateDoctorSpecialtyProfileMutation,
    useUpdateDoctorCvProfileMutation,
    useUpdateDoctorContactProfileMutation,
    useUpdateDoctorIdentityProfileMutation,
    useUpdateDoctorLocalizationProfileMutation,
    useLazyGetDoctorProfileQuery,
} from "../../../store/api/doctorApiSlice";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useLazyGetProfessionByIdQuery, useGetProfessionsQuery } from "../../../store/api/professionApiSlice";
import { setSelectedProfession } from "../../../store/slices/professionSlice";
import { setProfile } from "../../../store/slices/doctorSlice";
import { setDoctorProfile } from "../../../store/slices/authSlice";
import { setUiDisabled } from "../../../store/slices/appSlice";
import { sanitizeForRedux, formatBirthDateOnly } from "../../../utils/dateUtils";
import {
    buildContactProfilePayload,
    buildIdentityProfilePayload,
    buildLocalizationProfilePayload,
    buildCvProfilePayload,
    buildSpecialtyProfilePayload,
    getDoctorProfileErrorMessage,
} from "../../../utils/doctorProfileApi";
import {
    buildDoctorTaxonomyWriteFields,
    getProfession,
    getProfessionId,
    getProfessionIsEnabiz,
    getProfessionName,
    getPrimarySpecialtyId,
    getTaxonomyEntityName,
} from "../../../utils/taxonomyFields";
import { EMPTY_UUID } from "../../../constants/appConstants";
import { Loading } from "../../../components";
import { appColors } from "../../../constants/theme";
import {
    parseSpecialtyCategoryDetailResponse,
    toBranchScopedStepperDetail,
    normalizeDetailForStepper,
    mergeTitleAndBranchLists,
    detailNeedsTitleOrBranchLists,
    getUsableAttributeList,
    getUsableAttributeListLength,
    getAttributeItemList,
    getItemIdFromAttrItem,
    getDoctorAttributeItems,
    getSelectedAttributeItemId,
    toDoctorSpecialtyAttributeWriteItems,
    hasAtLeastOnePracticeAreaSelection,
} from "./professionPayloadUtils";
import { stepTitleSx } from "./style";

const DoctorInfoStepper = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const fromProfile = location.pathname.startsWith("/profile/doctor/info") || location.state?.from === "/profile";
    const mode = location.state?.mode; // "basicInfoOnly" | "specialtyOnly"
    const basicInfoOnly = fromProfile && mode === "basicInfoOnly";
    const specialtyOnly = mode === "specialtyOnly"; // anasayfa veya profilden "uzmanlık doldur" ile açıldığında true
    /** IncompleteProfile / Rejected: Specialty → Anket → Kimlik */
    const isRegistrationWizard = !specialtyOnly && !basicInfoOnly;

    const specialtyStepNumber = specialtyOnly ? 2 : 1;
    const attributeStartStep = specialtyOnly ? 3 : 2;
    const computeRegistrationTotalSteps = (attrCount) => 2 + (attrCount || 0);
    const getIdentityStepNumber = (attrCount) =>
        isRegistrationWizard ? computeRegistrationTotalSteps(attrCount) : null;

    const [step, setStep] = useState(1);
    const [totalSteps, setTotalSteps] = useState(1);
    const [tempCategoryId, setTempCategoryId] = useState("");
    const dispatch = useDispatch();
    const user = useSelector((state) => state.auth.user);
    const registerStepOneRef = useRef();
    const registerStepTwoRef = useRef();
    // Specialty category step'ler için her biri için ayrı ref
    const specialtyAttributeStepRefs = useRef({});
    /** Step 2: profilde branş önceden doluysa dropdown onChange tetiklenmez; scoped fetch için son eşitlenen category|branch anahtarı */
    const branchScopedSyncKeyRef = useRef("");
    const branchScopedSyncPendingRef = useRef(false);
    const stepperTopRef = useRef(null);

    const scrollStepperToTop = useCallback(() => {
        const run = () => {
            window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
            document.documentElement.scrollTo?.({ top: 0, behavior: "smooth" });
            document.body.scrollTo?.({ top: 0, behavior: "smooth" });

            // Layout main (overflowY: auto) gibi iç scroll container'ları da sıfırla
            let node = stepperTopRef.current?.parentElement;
            while (node && node !== document.body) {
                const style = window.getComputedStyle(node);
                const canScrollY =
                    (style.overflowY === "auto" || style.overflowY === "scroll") &&
                    node.scrollHeight > node.clientHeight;
                if (canScrollY) {
                    node.scrollTo({ top: 0, behavior: "smooth" });
                }
                node = node.parentElement;
            }

            stepperTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        };
        // Adım içeriği render olduktan sonra kaydır
        requestAnimationFrame(() => {
            requestAnimationFrame(run);
        });
    }, []);

    useEffect(() => {
        dispatch(setUiDisabled(true));
        return () => {
            dispatch(setUiDisabled(false));
        };
    }, [dispatch, fromProfile, mode, specialtyOnly, basicInfoOnly]);

    useEffect(() => {
        scrollStepperToTop();
    }, [step, scrollStepperToTop]);

    const nextStep = () => {
        setStep((prev) => Math.min(prev + 1, totalSteps));
    };
    const prevStep = () => {
        setStep((prev) => Math.max(prev - 1, 1));
    };

    const [updateDoctor] = useUpdateDoctorMutation();
    const [updateDoctorContactProfile] = useUpdateDoctorContactProfileMutation();
    const [updateDoctorIdentityProfile] = useUpdateDoctorIdentityProfileMutation();
    const [updateDoctorLocalizationProfile] = useUpdateDoctorLocalizationProfileMutation();
    const [updateDoctorSpecialtyProfile] = useUpdateDoctorSpecialtyProfileMutation();
    const [updateDoctorCvProfile] = useUpdateDoctorCvProfileMutation();
    const [triggerGetSpecialtyCategoryById] = useLazyGetProfessionByIdQuery();
    const selectedProfession = useSelector((state) => state.profession.selected);
    const profile = useSelector((state) => state.doctor.profile);
    const hasSpecialtyCategory = Boolean(getProfessionId(profile));
    const isDoctorCategory = selectedProfession?.isDoctorService ?? getProfession(profile)?.isDoctorService ?? false;
    const isEnabiz =
        selectedProfession?.isEnabiz ??
        selectedProfession?.IsEnabiz ??
        getProfessionIsEnabiz(profile) ??
        true;
    const { data: categoriesData } = useGetProfessionsQuery(undefined, { skip: !(specialtyOnly && step === 1 && !hasSpecialtyCategory) });
    const categories = categoriesData?.data ?? [];

    const [exitDialogOpen, setExitDialogOpen] = useState(false);

    const [, setLoading] = useState(false);
    const [getDoctorProfile] = useLazyGetDoctorProfileQuery();
    const [progressValue, setProgressValue] = useState((step / totalSteps) * 100);
    const [boxStyle, setBoxStyle] = useState({});
    const [eligibilityStatus, setEligibilityStatus] = useState(null);
    const [specialtyDataReady, setSpecialtyDataReady] = useState(false);
    const [branchQuestionsMissing, setBranchQuestionsMissing] = useState(false);
    const specialistFilesRef = useRef({ graduationPdfFile: null, certificateFiles: [] });
    const graduationCertificatesRef = useRef(null);

    const handleSubmit = async (profileOverride) => {
        const dataToSubmit = profileOverride || profile;
        try {
            // Onaylı / profilden iletişim-kimlik: doğrudan profile/contact (+ localization).
            // registration-update paketi kullanılmaz; uzmanlık ayrı specialty endpoint'i ile gider.
            if (fromProfile && basicInfoOnly) {
                setLoading(true);
                const contactRes = await updateDoctorContactProfile(
                    buildContactProfilePayload(dataToSubmit)
                ).unwrap();
                if (contactRes?.isSuccess === false) {
                    throw new Error(JSON.stringify(contactRes?.error));
                }

                const identityRes = await updateDoctorIdentityProfile(
                    buildIdentityProfilePayload(dataToSubmit)
                ).unwrap();
                if (identityRes?.isSuccess === false) {
                    throw new Error(JSON.stringify(identityRes?.error));
                }

                const hasLocalizationChange =
                    dataToSubmit?.city != null ||
                    dataToSubmit?.City != null ||
                    dataToSubmit?.timeZone != null;
                if (hasLocalizationChange) {
                    const locRes = await updateDoctorLocalizationProfile(
                        buildLocalizationProfilePayload({
                            ...dataToSubmit,
                            city: dataToSubmit?.city ?? dataToSubmit?.City,
                        })
                    ).unwrap();
                    if (locRes?.isSuccess === false) {
                        throw new Error(JSON.stringify(locRes?.error));
                    }
                }

                setLoading(false);
                const needsApproval = identityRes?.data?.requiresApproval === true;
                toast.success(
                    needsApproval
                        ? t("identity_update_sent_for_approval")
                        : t("profile_update_success")
                );
                navigate("/profile");
                return;
            }

            const isApprovedProfile =
                profile?.status === "Approved" || profile?.status === "UpdatePending";

            if (fromProfile && specialtyOnly && isApprovedProfile) {
                setLoading(true);
                const response = await updateDoctorSpecialtyProfile(
                    buildSpecialtyProfilePayload(dataToSubmit, { lockTaxonomy: true })
                ).unwrap();

                if (response.isSuccess && dataToSubmit?.about != null) {
                    const cvResponse = await updateDoctorCvProfile(
                        buildCvProfilePayload({
                            values: { ...profile, ...dataToSubmit },
                            educations: profile?.educations ?? [],
                            experiences: profile?.experiences ?? [],
                            publications: profile?.publications ?? [],
                            languageValues: profile?.languages ?? [],
                            about: dataToSubmit.about,
                        })
                    ).unwrap();
                    if (!cvResponse.isSuccess) {
                        throw new Error(JSON.stringify(cvResponse?.error));
                    }
                }

                setLoading(false);
                if (response.isSuccess) {
                    toast.success(t("profile_update_success"));
                    navigate("/profile", { replace: true });
                    return;
                }
                throw new Error(JSON.stringify(response?.error));
            }

            const formData = new FormData();
            const doctorId = dataToSubmit?.id ?? profile?.id ?? user?.id;
            if (doctorId) formData.append("Id", doctorId);
            const flat = { ...dataToSubmit };
            // Backend DTO IdentitySerialNo MaxLength(4) istiyor. Specialty-only düzenlemede
            // profile içindeki seriNo yanlış eşleşebiliyor (11 haneli geliyor). Bu yüzden geçersizse form-data'ya ekleme.
            if (flat?.identitySerialNo != null && String(flat.identitySerialNo).length > 4) {
                flat.identitySerialNo = undefined;
            }
            if (flat.birthDate != null && flat.birthDate !== '') {
                flat.birthDate = formatBirthDateOnly(flat.birthDate) ?? '';
            }
            if (!flat.titleId && flat.title?.id) flat.titleId = flat.title.id;
            const taxonomyWrite = buildDoctorTaxonomyWriteFields(flat);
            flat.professionId = taxonomyWrite.professionId;
            flat.primarySpecialtyId = taxonomyWrite.primarySpecialtyId;
            flat.titleId = taxonomyWrite.titleId ?? flat.titleId;
            delete flat.specialtyCategoryId;
            delete flat.branchId;
            delete flat.SpecialtyCategoryId;
            delete flat.BranchId;
            const skipKeys = [
                "graduationCertificates",
                "billingInfo",
                "doctorSpecialtyAttributeItems",
                "doctorSpecialtyCategoryAttributeItems",
                "title",
                "branch",
                "profession",
                "primarySpecialty",
                "specialtyCategory",
                "graduationPdfFile",
                "certificateFiles",
                "id",
                "specialtyCategoryId",
                "branchId",
            ];
            if (!isDoctorCategory) skipKeys.push("diplomaNo");
            Object.entries(flat).forEach(([key, value]) => {
                if (skipKeys.includes(key) || value === undefined) return;
                if (value !== null && typeof value === "object" && !Array.isArray(value) && !(value instanceof File)) return;
                formData.append(key, value ?? "");
            });
            const graduationFile = dataToSubmit?.graduationPdfFile ?? specialistFilesRef.current?.graduationPdfFile;
            const certificateFilesList = dataToSubmit?.certificateFiles ?? specialistFilesRef.current?.certificateFiles;
            if (!isDoctorCategory && graduationFile && graduationFile instanceof File) {
                formData.append("GraduationPdfFile", graduationFile);
            }
            if (!isDoctorCategory && Array.isArray(certificateFilesList)) {
                certificateFilesList.forEach((file) => {
                    if (file && file instanceof File) formData.append("CertificateFiles", file);
                });
            }
            const removedCerts = specialistFilesRef.current?.removedCertificateFileNames;
            if (!isDoctorCategory && Array.isArray(removedCerts)) {
                removedCerts.forEach((fileName) => {
                    formData.append("RemovedCertificateFileNames", fileName);
                });
            }

            const graduationCertificatesToSend = Array.isArray(dataToSubmit.graduationCertificates) && dataToSubmit.graduationCertificates.length > 0
                ? dataToSubmit.graduationCertificates
                : graduationCertificatesRef.current;
            if (Array.isArray(graduationCertificatesToSend) && graduationCertificatesToSend.length > 0) {
                graduationCertificatesToSend.forEach((cert, idx) => {
                    Object.entries(cert).forEach(([cKey, cValue]) => {
                        if (cKey === "graduationFile") {
                            if (cValue instanceof File) formData.append(`graduationCertificates[${idx}].graduationFile`, cValue);
                        } else if (cValue !== undefined && cValue !== null) {
                            formData.append(`graduationCertificates[${idx}].${cKey}`, cValue);
                        }
                    });
                });
            }

            if (dataToSubmit.billingInfo) {
                Object.entries(dataToSubmit.billingInfo).forEach(([bKey, bValue]) => {
                    formData.append(`billingInfo.${bKey}`, bValue);
                });
            }

            const writeAttrItems = toDoctorSpecialtyAttributeWriteItems(getDoctorAttributeItems(dataToSubmit));
            writeAttrItems.forEach((item, idx) => {
                formData.append(
                    `doctorSpecialtyAttributeItems[${idx}].specialtyAttributeItemId`,
                    String(item.specialtyAttributeItemId)
                );
            });

            setLoading(true);
            const response = await updateDoctor(formData).unwrap();
            setLoading(false);
            if (response.isSuccess) {
                toast.success(t("profile_update_success"));
                if (fromProfile || specialtyOnly) {
                    navigate(location.state?.from === "/" ? "/" : "/profile", { replace: true });
                    return;
                }
                try {
                    const profileRes = await getDoctorProfile().unwrap();
                    if (profileRes?.isSuccess && profileRes?.data) {
                        dispatch(setDoctorProfile({ ...profileRes.data, isRegistrationCompleted: true }));
                    }
                } catch (e) {
                    console.warn("Refetch profile after update failed", e);
                }
                navigate("/", { replace: true });
            } else {
                setLoading(false);
                throw new Error(JSON.stringify(response.error));
            }
        } catch (error) {
            setLoading(false);
            console.error(error);
            const data = error?.data;
            const errMsg = data?.error?.value ?? data?.error?.message ?? (typeof data?.error === "string" ? data.error : null);
            const message = getDoctorProfileErrorMessage(error, t);
            toast.error(message || errMsg || t("profile_update_error"));
        }
    };

    /** Branşlı/branşsız uzmanlık detayı: önce scoped GET, listeler eksikse veya hata varsa tam GET ile ünvan/branş birleştirilir. */
    const fetchProfessionForStepper = useCallback(
        async (categoryId, branchId) => {
            if (!categoryId) return null;
            let detailPayload = null;
            const hasBranch = Boolean(branchId);
            try {
                const res = await triggerGetSpecialtyCategoryById(
                    hasBranch ? { id: categoryId, specialtyId: branchId } : { id: categoryId }
                ).unwrap();
                const parsed = parseSpecialtyCategoryDetailResponse(res);
                if (parsed.ok && parsed.payload) {
                    const p = parsed.payload;
                    detailPayload = hasBranch
                        ? toBranchScopedStepperDetail(p, branchId)
                        : normalizeDetailForStepper(p);
                    detailPayload = mergeTitleAndBranchLists(detailPayload, p);
                }
            } catch (e) {
                console.warn("Specialty category fetch failed", e);
            }
            if (hasBranch && (!detailPayload || detailNeedsTitleOrBranchLists(detailPayload))) {
                try {
                    const resFb = await triggerGetSpecialtyCategoryById({ id: categoryId }).unwrap();
                    const parsedFb = parseSpecialtyCategoryDetailResponse(resFb);
                    if (parsedFb.ok && parsedFb.payload) {
                        const full = parsedFb.payload;
                        if (!detailPayload) {
                            detailPayload = toBranchScopedStepperDetail(full, branchId);
                        }
                        detailPayload = mergeTitleAndBranchLists(detailPayload, full);
                    }
                } catch (e2) {
                    console.warn("Specialty category fallback fetch failed", e2);
                }
            }
            return detailPayload;
        },
        [triggerGetSpecialtyCategoryById]
    );

    const handleBranchIdChange = useCallback(
        async (newBranchId) => {
            const categoryId = selectedProfession?.id ?? getProfessionId(profile);
            if (!categoryId || !newBranchId) return;
            setLoading(true);
            try {
                const branchScopedPayload = await fetchProfessionForStepper(
                    categoryId,
                    newBranchId
                );
                branchScopedSyncKeyRef.current = `${categoryId}|${newBranchId}`;
                if (!branchScopedPayload) {
                    toast.error(t("specialty_category_detail_load_failed"));
                    setLoading(false);
                    return;
                }
                const branchAttrList = getUsableAttributeList(branchScopedPayload);
                dispatch(
                    setProfile(
                        sanitizeForRedux({
                            ...profile,
                            primarySpecialtyId: newBranchId,
                            doctorSpecialtyAttributeItems: [],
                        })
                    )
                );
                if (!branchAttrList.length) {
                    setBranchQuestionsMissing(true);
                    toast.error(
                        "Seçilen uzmanlık için anket soruları bulunamadı. Lütfen uzmanlığı veya mesleği kontrol edin."
                    );
                    dispatch(setSelectedProfession(branchScopedPayload));
                    setTotalSteps(
                        isRegistrationWizard
                            ? computeRegistrationTotalSteps(0)
                            : specialtyStepNumber
                    );
                    return;
                }
                setBranchQuestionsMissing(false);
                dispatch(setSelectedProfession(branchScopedPayload));
                const attrCount = getUsableAttributeListLength(branchScopedPayload);
                setTotalSteps(
                    isRegistrationWizard
                        ? computeRegistrationTotalSteps(attrCount)
                        : specialtyStepNumber + attrCount
                );
            } catch (e) {
                console.warn("Branch scoped specialty fetch on branch change failed", e);
                if (categoryId && newBranchId) {
                    branchScopedSyncKeyRef.current = `${categoryId}|${newBranchId}`;
                }
                toast.error(t("specialty_category_detail_load_failed"));
            } finally {
                setLoading(false);
            }
        },
        [dispatch, fetchProfessionForStepper, isRegistrationWizard, profile, selectedProfession?.id, specialtyStepNumber, t]
    );

    useEffect(() => {
        if (step !== specialtyStepNumber) return;
        const bid = getPrimarySpecialtyId(profile);
        const categoryId = selectedProfession?.id ?? getProfessionId(profile);
        if (!categoryId || !bid) return;
        const key = `${categoryId}|${bid}`;
        if (branchScopedSyncKeyRef.current === key) return;
        if (branchScopedSyncPendingRef.current) return;
        branchScopedSyncPendingRef.current = true;
        void handleBranchIdChange(String(bid)).finally(() => {
            branchScopedSyncPendingRef.current = false;
        });
    }, [step, specialtyStepNumber, selectedProfession?.id, profile?.branchId, profile?.branch?.id, profile?.primarySpecialtyId, profile?.primarySpecialty?.id, profile?.professionId, handleBranchIdChange]);

    const handleStepSuccess = (values) => {
        if (values.graduationPdfFile && values.graduationPdfFile instanceof File) {
            specialistFilesRef.current.graduationPdfFile = values.graduationPdfFile;
        }
        if (values.removedGraduationPdf) {
            specialistFilesRef.current.graduationPdfFile = null;
        }
        if (Array.isArray(values.certificateFiles)) {
            specialistFilesRef.current.certificateFiles = values.certificateFiles.filter((f) => f && f instanceof File);
        }
        if (Array.isArray(values.removedCertificateFileNames) && values.removedCertificateFileNames.length > 0) {
            specialistFilesRef.current.removedCertificateFileNames = values.removedCertificateFileNames;
        }
        if (Array.isArray(values.graduationCertificates) && values.graduationCertificates.length > 0) {
            graduationCertificatesRef.current = values.graduationCertificates;
        }
        const nextAttrItems =
            values.doctorSpecialtyAttributeItems ??
            values.doctorSpecialtyCategoryAttributeItems;
        const updatedProfile = {
            ...profile,
            ...values,
            ...(values.billingInfo ? { billingInfo: values.billingInfo } : {}),
            ...(nextAttrItems
                ? { doctorSpecialtyAttributeItems: nextAttrItems }
                : {}),
            ...(values.graduationCertificates ? {
                graduationCertificates: values.graduationCertificates
            } : {}),
        };

        dispatch(setProfile(sanitizeForRedux(updatedProfile)));

        let profileAfterBranchFetch = updatedProfile;
        let effectiveTotalSteps = totalSteps;
        const proceed = async () => {
            let branchFetchFailed = false;
            const selectedSpecialtyId = values?.primarySpecialtyId ?? values?.branchId;
            const categoryIdForFetch = selectedProfession?.id ?? getProfessionId(profileAfterBranchFetch);
            if (step === specialtyStepNumber && categoryIdForFetch && selectedSpecialtyId) {
                try {
                    const branchScopedPayload = await fetchProfessionForStepper(
                        categoryIdForFetch,
                        selectedSpecialtyId
                    );
                    if (!branchScopedPayload) {
                        branchFetchFailed = true;
                        toast.error(t("specialty_category_detail_load_failed"));
                    } else {
                        const branchAttrList = getUsableAttributeList(branchScopedPayload);
                        if (!branchAttrList.length) {
                            setBranchQuestionsMissing(true);
                            toast.error(
                                "Seçilen uzmanlık için anket soruları bulunamadı. Lütfen uzmanlığı veya mesleği kontrol edin."
                            );
                            return;
                        }
                        setBranchQuestionsMissing(false);
                        const validItemIds = new Set(
                            branchAttrList.flatMap((attr) =>
                                getAttributeItemList(attr)
                                    .map((it) => getItemIdFromAttrItem(it))
                                    .filter(
                                        (id) =>
                                            id != null &&
                                            String(id).toLowerCase() !== EMPTY_UUID.toLowerCase()
                                    )
                                    .map((id) => String(id).toLowerCase())
                            )
                        );
                        const pruned = getDoctorAttributeItems(updatedProfile).filter((row) => {
                            const raw = getSelectedAttributeItemId(row);
                            if (raw == null || raw === "") return false;
                            return validItemIds.has(String(raw).toLowerCase());
                        });
                        profileAfterBranchFetch = {
                            ...updatedProfile,
                            doctorSpecialtyAttributeItems: pruned,
                        };
                        dispatch(setProfile(sanitizeForRedux(profileAfterBranchFetch)));
                        dispatch(setSelectedProfession(branchScopedPayload));
                        const attrCount = getUsableAttributeListLength(branchScopedPayload);
                        effectiveTotalSteps = isRegistrationWizard
                            ? computeRegistrationTotalSteps(attrCount)
                            : specialtyStepNumber + attrCount;
                        setTotalSteps(effectiveTotalSteps);
                    }
                } catch (e) {
                    console.warn("Branch scoped specialty fetch failed", e);
                    branchFetchFailed = true;
                    toast.error(t("specialty_category_detail_load_failed"));
                }
            }
            if (branchFetchFailed) return;

            if (step === effectiveTotalSteps) {
                if (
                    !hasAtLeastOnePracticeAreaSelection(
                        selectedProfession,
                        getDoctorAttributeItems(profileAfterBranchFetch)
                    )
                ) {
                    toast.error(
                        "Onay için en az bir çalışma / hastalık alanı (PracticeArea) seçmelisiniz."
                    );
                    return;
                }
                handleSubmit(profileAfterBranchFetch);
            } else {
                nextStep();
            }
        };
        proceed();
    };

    const handleNext = async () => {
        if (specialtyOnly && step === 1 && !hasSpecialtyCategory) {
            if (!tempCategoryId) {
                toast.error(t("please_select_specialty_category"));
                return;
            }
            setLoading(true);
            try {
                const normalized = await fetchProfessionForStepper(tempCategoryId);
                if (normalized) {
                    dispatch(setSelectedProfession(normalized));
                    setTotalSteps(specialtyStepNumber);
                    setStep(specialtyStepNumber);
                    dispatch(setProfile(sanitizeForRedux({ ...profile, professionId: tempCategoryId })));
                } else {
                    toast.error(t("specialty_category_detail_load_failed"));
                }
            } catch (e) {
                toast.error(t("specialty_category_detail_load_failed"));
            } finally {
                setLoading(false);
            }
            return;
        }
        if (basicInfoOnly && step === 1 && registerStepOneRef.current) {
            registerStepOneRef.current.handleSubmit();
            return;
        }
        if (isRegistrationWizard && step === totalSteps && registerStepOneRef.current) {
            registerStepOneRef.current.handleSubmit();
            return;
        }
        if (step === specialtyStepNumber && registerStepTwoRef.current) {
            registerStepTwoRef.current.handleSubmit();
            return;
        }
        const attrCount = getUsableAttributeListLength(selectedProfession);
        const attrEndStep = attributeStartStep + attrCount - 1;
        if (step >= attributeStartStep && step <= attrEndStep) {
            const currentRef = specialtyAttributeStepRefs.current[step];
            if (currentRef && currentRef.handleSubmit) {
                currentRef.handleSubmit();
                return;
            }
        }
        nextStep();
    };

    const handleExit = () => {
        if (fromProfile) {
            navigate("/profile");
            return;
        }
        if (specialtyOnly && location.state?.from === "/") {
            navigate("/");
            return;
        }
        setExitDialogOpen(true);
    };

    const confirmExit = () => {
        const from = location.state?.from || null;
        setExitDialogOpen(false);
        if (from === "/profile") {
            handleSubmit();
            return;
        }
        if (from === "/") {
            navigate("/");
            return;
        }
        navigate('/logout');
    };

    const cancelExit = () => {
        setExitDialogOpen(false);
    };

    // Profile değişikliklerini takip etmek için memoized initial values
    const memoizedInitialValues = useMemo(() => {
        return profile ? { ...profile } : {};
    }, [profile]);

    const handleEligibilityResult = (status) => {
        setEligibilityStatus(status);
    };

    const handleEligibilityCheck = async () => {
        if (registerStepOneRef.current?.checkEligibility) {
            const result = await registerStepOneRef.current.checkEligibility();
            setEligibilityStatus(result === true ? true : false);
        }
    };

    const renderStepContent = () => {
        if (basicInfoOnly) {
            return (
                <Box sx={{ display: step === 1 ? "block" : "none" }}>
                    <RegisterStepOne
                        ref={registerStepOneRef}
                        onSuccess={handleStepSuccess}
                        onLoadingChange={setLoading}
                        onEligibilityResult={handleEligibilityResult}
                        initialValues={memoizedInitialValues}
                        step={1}
                        isActive={step === 1}
                        isDoctorCategory={isDoctorCategory}
                        isEnabiz={isEnabiz}
                    />
                </Box>
            );
        }
        if (specialtyOnly && step === 1 && !hasSpecialtyCategory) {
            return (
                <Box sx={{ p: 2 }}>
                    <Typography variant="h6" sx={stepTitleSx}>{t("select_specialty_category")}</Typography>
                    <TextField
                        select
                        fullWidth
                        label={t("specialty_category")}
                        value={tempCategoryId}
                        onChange={(e) => setTempCategoryId(e.target.value)}
                        sx={{ maxWidth: 400 }}
                    >
                        <MenuItem value="">{t("select")}</MenuItem>
                        {categories.map((cat) => (
                            <MenuItem key={cat.id} value={cat.id}>
                                {getTaxonomyEntityName(cat) || cat.id}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>
            );
        }
        const usableAttrs = getUsableAttributeList(selectedProfession);
        const specialtyAttributeSteps = [];
        for (let i = 0; i < usableAttrs.length; i++) {
            const attribute = usableAttrs[i];
            const stepNumber = attributeStartStep + i;
            specialtyAttributeSteps.push(
                <Box
                    key={`specialty-step-${stepNumber}`}
                    sx={{ display: step === stepNumber ? 'block' : 'none' }}
                >
                    <SpecialtyAttributeStep
                        specialtyAttribute={attribute}
                        ref={(el) => {
                            if (el) {
                                specialtyAttributeStepRefs.current[stepNumber] = el;
                            }
                        }}
                        onSuccess={handleStepSuccess}
                        onLoadingChange={setLoading}
                        initialValues={memoizedInitialValues}
                        step={stepNumber}
                        isActive={step === stepNumber}
                    />
                </Box>
            );
        }

        const identityStep = isRegistrationWizard
            ? getIdentityStepNumber(usableAttrs.length)
            : null;

        return (
            <>
                <Box sx={{ display: step === specialtyStepNumber ? "block" : "none" }}>
                    <RegisterStepTwo
                        ref={registerStepTwoRef}
                        onSuccess={handleStepSuccess}
                        onLoadingChange={setLoading}
                        onBranchIdChange={handleBranchIdChange}
                        initialValues={memoizedInitialValues}
                        step={specialtyStepNumber}
                        isActive={step === specialtyStepNumber}
                    />
                </Box>
                {specialtyAttributeSteps}
                {isRegistrationWizard && identityStep != null && (
                    <Box sx={{ display: step === identityStep ? "block" : "none" }}>
                        <RegisterStepOne
                            ref={registerStepOneRef}
                            onSuccess={handleStepSuccess}
                            onLoadingChange={setLoading}
                            onEligibilityResult={handleEligibilityResult}
                            initialValues={memoizedInitialValues}
                            step={identityStep}
                            isActive={step === identityStep}
                            isDoctorCategory={isDoctorCategory}
                            isEnabiz={isEnabiz}
                        />
                    </Box>
                )}
            </>
        );
    };

    const getStepLabel = () => {
        if (specialtyOnly && step === 1 && !hasSpecialtyCategory) return t("specialty_category");
        if (basicInfoOnly && step === 1) return t("Temel Bilgiler");
        if (step === specialtyStepNumber) return t("Uzmanlık Bilgileri");
        const attrCount = getUsableAttributeListLength(selectedProfession);
        const identityStep = getIdentityStepNumber(attrCount);
        if (isRegistrationWizard && identityStep != null && step === identityStep) {
            return t("Temel Bilgiler");
        }
        if (step >= attributeStartStep && step < attributeStartStep + attrCount) {
            const attributeIndex = step - attributeStartStep;
            const attribute = getUsableAttributeList(selectedProfession)?.[attributeIndex];
            return (
                attribute?.attributeDefinition ||
                attribute?.AttributeDefinition ||
                attribute?.attributeTitle ||
                attribute?.title ||
                t("Ek Bilgi")
            );
        }
        return "";
    };

    const displayStep = specialtyOnly && hasSpecialtyCategory ? step - 1 : step;
    const displayTotal = specialtyOnly && hasSpecialtyCategory ? totalSteps - 1 : totalSteps;

    const getSpecialtyCategoryDatas = useCallback(async () => {
        setLoading(true);
        try {
            const resDoctor = await getDoctorProfile().unwrap();
            if (resDoctor.isSuccess === false) {
                setLoading(false);
                throw new Error(t("error_occured"));
            }
            const data = resDoctor.data;
            dispatch(setProfile(sanitizeForRedux(data)));

            if (basicInfoOnly) {
                setTotalSteps(1);
                setStep(1);
                setLoading(false);
                return;
            }

            if (specialtyOnly) {
                const categoryId = getProfessionId(data);
                if (!categoryId) {
                    setTotalSteps(1);
                    setStep(1);
                    setSpecialtyDataReady(true);
                    setLoading(false);
                    return;
                }
                const branchId = getPrimarySpecialtyId(data);
                const detailPayload = await fetchProfessionForStepper(categoryId, branchId || undefined);
                if (detailPayload) {
                    dispatch(setSelectedProfession(detailPayload));
                    if (branchId && categoryId) {
                        branchScopedSyncKeyRef.current = `${categoryId}|${branchId}`;
                    }
                    const attrCount = getUsableAttributeListLength(detailPayload);
                    setBranchQuestionsMissing(Boolean(branchId) && attrCount === 0);
                    if (branchId && attrCount === 0) {
                        toast.error(
                            "Seçilen uzmanlık için anket soruları bulunamadı. Lütfen uzmanlığı veya mesleği kontrol edin."
                        );
                    }
                    setTotalSteps(specialtyStepNumber + (branchId ? attrCount : 0));
                } else {
                    toast.error(t("specialty_category_detail_load_failed"));
                    setBranchQuestionsMissing(false);
                    setTotalSteps(specialtyStepNumber);
                }
                setStep(specialtyStepNumber);
                setSpecialtyDataReady(true);
                setLoading(false);
                return;
            }

            // IncompleteProfile / Rejected: Specialty → Anket → Kimlik
            const cid = getProfessionId(data);
            if (!cid) {
                toast.error(t("specialty_category_detail_load_failed"));
                setTotalSteps(computeRegistrationTotalSteps(0));
                setStep(1);
                setLoading(false);
                return;
            }
            const bid = getPrimarySpecialtyId(data);
            const normalizedPayload = await fetchProfessionForStepper(cid, bid || undefined);
            if (normalizedPayload) {
                dispatch(setSelectedProfession(normalizedPayload));
                if (bid) {
                    branchScopedSyncKeyRef.current = `${cid}|${bid}`;
                }
                const attrCount = bid ? getUsableAttributeListLength(normalizedPayload) : 0;
                setBranchQuestionsMissing(Boolean(bid) && attrCount === 0);
                setTotalSteps(computeRegistrationTotalSteps(attrCount));
            } else {
                toast.error(t("specialty_category_detail_load_failed"));
                setTotalSteps(computeRegistrationTotalSteps(0));
            }
            setStep(1);
            setLoading(false);
        } catch (err) {
            if (specialtyOnly) setSpecialtyDataReady(true);
            setLoading(false);
            toast.error(t("error_occured"));
        }
    }, [basicInfoOnly, dispatch, fetchProfessionForStepper, getDoctorProfile, specialtyOnly, specialtyStepNumber, t]);

    useEffect(() => {
        getSpecialtyCategoryDatas();
    }, [getSpecialtyCategoryDatas]);

    useEffect(() => {
        const dStep = specialtyOnly && hasSpecialtyCategory ? step - 1 : step;
        const dTotal = specialtyOnly && hasSpecialtyCategory ? totalSteps - 1 : totalSteps;
        setProgressValue(dTotal > 0 ? (dStep / dTotal) * 100 : 0);
    }, [step, totalSteps, specialtyOnly, hasSpecialtyCategory]);

    useEffect(() => {
        if (fromProfile) {
            setBoxStyle({});
            return undefined;
        }
        const updateBoxPosition = () => {
            const screenHeight = window.innerHeight;
            const estimatedBoxHeight = 600;

            if (estimatedBoxHeight < screenHeight - 90) {
                setBoxStyle({
                    margin: 'auto',
                    marginBottom: '50px'
                });
            } else {
                setBoxStyle({
                    marginTop: '20px',
                    marginBottom: '50px'
                });
            }
        };

        updateBoxPosition();
        window.addEventListener('resize', updateBoxPosition);

        return () => {
            window.removeEventListener('resize', updateBoxPosition);
        };
    }, [fromProfile]);




    if (specialtyOnly && !specialtyDataReady) {
        return <Loading backdrop />;
    }

    return (
        <>
            <Box
                ref={stepperTopRef}
                sx={{
                    minHeight: { xs: "100vh", md: "100dvh" },
                    width: "100%",
                    backgroundColor: appColors.bgMuted,
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    position: "relative",
                    px: { xs: 1.5, sm: 2 },
                    pt: { xs: 1.5, sm: 2.5 },
                    pb: { xs: 3, sm: 5 },
                    boxSizing: "border-box",
                    // Layout main altında beyaz boşluk kalmasın
                    flexGrow: 1,
                    alignSelf: "stretch",
                }}
            >
                <Container maxWidth="lg" sx={{ px: { xs: 0, sm: 2 } }}>
                    <Fade in timeout={800}>
                        <Box
                            sx={{
                                width: "100%",
                                maxWidth: "960px",
                                mx: "auto",
                                borderRadius: { xs: "16px", sm: "24px" },
                                overflow: "hidden",
                                background: appColors.white,
                                position: "relative",
                                display: "flex",
                                flexDirection: "column",
                                mb: 2,
                                ...boxStyle,
                            }}
                        >
                            {/* Header Section */}
                            <Box sx={{
                                background: `linear-gradient(135deg, ${appColors.primary} 0%, ${appColors.primaryTeal} 100%)`,
                                px: { xs: 2, sm: 3, md: 4 },
                                py: fromProfile ? { xs: 2, sm: 2.5 } : { xs: 2.5, sm: 3.5 },
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                position: "relative",
                                flexShrink: 0,
                            }}>
                                <IconButton
                                    onClick={handleExit}
                                    sx={{
                                        position: "absolute",
                                        top: 12,
                                        right: 12,
                                        color: appColors.white,
                                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                                        "&:hover": {
                                            backgroundColor: "rgba(255, 255, 255, 0.2)",
                                        },
                                        transition: "all 0.2s ease",
                                        zIndex: 10,
                                    }}
                                >
                                    <Close sx={{ fontSize: 20 }} />
                                </IconButton>

                                {/* Logo */}
                                <Box sx={{
                                    width: fromProfile ? 40 : 48,
                                    height: fromProfile ? 40 : 48,
                                    borderRadius: "12px",
                                    background: "rgba(255, 255, 255, 0.2)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: appColors.white,
                                }}>
                                    <Typography sx={{ fontSize: fromProfile ? "22px" : "28px", fontWeight: "bold" }}>M</Typography>
                                </Box>

                                {/* Title */}
                                <Typography
                                    variant="h4"
                                    sx={{
                                        color: appColors.white,
                                        fontWeight: 700,
                                        fontSize: fromProfile ? { xs: "1.35rem", sm: "1.5rem" } : { xs: "1.5rem", sm: "2rem" },
                                        lineHeight: 1.2,
                                        marginBottom: "0.25rem",
                                        marginTop: fromProfile ? "0.75rem" : "1rem"
                                    }}
                                >
                                    Medilya
                                </Typography>

                                {/* Subtitle */}
                                <Typography
                                    sx={{
                                        color: appColors.white,
                                        fontWeight: 600,
                                        fontSize: fromProfile ? "1rem" : { xs: "1.05rem", sm: "1.25rem" },
                                        margin: 0,
                                        opacity: 0.9,
                                        textAlign: "center",
                                        px: 1,
                                    }}
                                >
                                    {getTaxonomyEntityName(selectedProfession) || getProfessionName(selectedProfession)
                                        ? `${getTaxonomyEntityName(selectedProfession) || getProfessionName(selectedProfession)} Uzman Kayıt`
                                        : "Uzman Kayıt"}
                                </Typography>

                                {/* Description */}
                                {!fromProfile && (
                                <Typography
                                    sx={{
                                        color: appColors.white,
                                        fontSize: "0.875rem",
                                        margin: 0,
                                        opacity: 0.8,
                                    }}
                                >
                                    Danışan takip sisteminize hızlı kayıt
                                </Typography>
                                )}
                            </Box>

                            {/* Progress Section */}
                            <Box sx={{
                                px: { xs: 2, sm: 3, md: 4 },
                                pt: { xs: 1.5, sm: 2 },
                                pb: 1,
                                display: "flex",
                                alignItems: "center",
                                gap: { xs: 0.75, sm: 1.5 },
                                flexWrap: "wrap",
                                flexShrink: 0,
                            }}>
                                <Typography
                                    sx={{
                                        fontWeight: "600",
                                        fontSize: { xs: "0.875rem", sm: "1rem" },
                                        color: appColors.gray900,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    Adım {displayStep} / {displayTotal}
                                </Typography>

                                <Box sx={{ flex: 1, minWidth: 80 }}>
                                    <LinearProgress
                                        variant="determinate"
                                        value={progressValue}
                                        sx={{
                                            height: 8,
                                            borderRadius: 4,
                                            backgroundColor: appColors.gray200,
                                            "& .MuiLinearProgress-bar": {
                                                background: appColors.primary,
                                                borderRadius: 4,
                                            },
                                        }}
                                    />
                                </Box>

                                <Typography
                                    sx={{
                                        fontWeight: "500",
                                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                        color: appColors.gray500,
                                        maxWidth: { xs: "100%", sm: 220 },
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {getStepLabel()}
                                </Typography>
                            </Box>

                            {/* Form Content */}
                            <Box sx={{
                                px: { xs: 2, sm: 3, md: 4 },
                                pt: 1,
                                pb: 1,
                                width: "100%",
                                maxWidth: "100%",
                                boxSizing: "border-box",
                                overflowX: "hidden",
                            }}>
                                {renderStepContent()}
                            </Box>

                            {/* Navigation Buttons */}
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: 1.5,
                                    flexWrap: "wrap",
                                    px: { xs: 2, sm: 3, md: 4 },
                                    pt: 1.5,
                                    pb: fromProfile ? 2.5 : 1.5,
                                    flexShrink: 0,
                                }}
                            >
                                {/* */}

                                {/* Önceki butonu - sadece ilk adımda değilse göster */}
                                {step > 1 && (
                                    <Button
                                        variant="outlined"
                                        onClick={prevStep}
                                        sx={{
                                            minWidth: 120,
                                            borderColor: appColors.primary,
                                            color: appColors.primary,
                                            "&:hover": {
                                                borderColor: appColors.primaryDark,
                                                backgroundColor: "rgba(13, 148, 136, 0.08)",
                                            }
                                        }}
                                    >
                                        {t("back")}
                                    </Button>
                                )}

                                {/* Sağ taraftaki butonlar */}
                                {((basicInfoOnly && step === 1) ||
                                    (isRegistrationWizard &&
                                        step ===
                                            getIdentityStepNumber(
                                                getUsableAttributeListLength(selectedProfession)
                                            ))) &&
                                    isDoctorCategory &&
                                    isEnabiz && (
                                    <Box sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        flex: 1,
                                        minWidth: 0,
                                        flexBasis: { xs: "100%", sm: "auto" },
                                    }}>
                                        {eligibilityStatus === null ? (
                                            <Typography sx={{ color: appColors.gray500, fontSize: "0.85rem", fontWeight: 500 }}>
                                                Uzman uygunluğunu kontrol ettikten sonra kayıt işlemine devam edebilirsiniz...
                                            </Typography>
                                        ) : eligibilityStatus ? (
                                                <>
                                                    <CheckCircle sx={{ color: appColors.success, fontSize: 18, flexShrink: 0 }} />
                                                    <Typography sx={{ color: appColors.successDark, fontSize: "0.85rem", fontWeight: 500 }}>
                                                        Uzaktan sağlık hizmeti vermeye yetkiniz var.
                                                    </Typography>
                                                </>
                                        ) : (
                                            <>
                                                <Cancel sx={{ color: appColors.error, fontSize: 18, flexShrink: 0 }} />
                                                <Typography sx={{ color: appColors.error, fontSize: "0.85rem", fontWeight: 500 }}>
                                                    Doktor uzaktan sağlık hizmeti vermeye uygun değildir. Lütfen gerekli işlemleri yaptıktan sonra tekrar deneyin.
                                                </Typography>
                                            </>
                                        )}
                                    </Box>
                                )}
                                <Box sx={{ display: "flex", gap: 1.5, ml: { xs: 0, sm: "auto" }, alignItems: "center", flexWrap: "wrap", width: { xs: "100%", sm: "auto" }, justifyContent: { xs: "stretch", sm: "flex-end" } }}>
                                    {/* Son adımda kaydet butonu, diğerlerinde ileri butonu */}
                                    {step === totalSteps ? (
                                        <>
                                            {((basicInfoOnly && step === 1) ||
                                                (isRegistrationWizard &&
                                                    step ===
                                                        getIdentityStepNumber(
                                                            getUsableAttributeListLength(selectedProfession)
                                                        ))) &&
                                                isDoctorCategory &&
                                                isEnabiz && (
                                                <Button
                                                    variant="outlined"
                                                    onClick={handleEligibilityCheck}
                                                    sx={{
                                                        minWidth: 180,
                                                        borderColor: appColors.primary,
                                                        color: appColors.primary,
                                                        borderRadius: "12px",
                                                        textTransform: "none",
                                                        fontWeight: 600,
                                                        "&:hover": {
                                                            borderColor: appColors.primaryDark,
                                                            backgroundColor: "rgba(13, 148, 136, 0.08)",
                                                        },
                                                    }}
                                                >
                                                    {t("check_specialist_eligibility")}
                                                </Button>
                                            )}
                                            <Button
                                                onClick={handleNext}
                                                variant="contained"
                                                disabled={
                                                    (((basicInfoOnly && step === 1) ||
                                                        (isRegistrationWizard &&
                                                            step ===
                                                                getIdentityStepNumber(
                                                                    getUsableAttributeListLength(selectedProfession)
                                                                ))) &&
                                                        isDoctorCategory &&
                                                        isEnabiz &&
                                                        eligibilityStatus !== true) ||
                                                    (step === specialtyStepNumber && branchQuestionsMissing)
                                                }
                                                sx={{
                                                    minWidth: { xs: 0, sm: 120 },
                                                    flex: { xs: 1, sm: "none" },
                                                    background: `linear-gradient(135deg, ${appColors.primary} 0%, ${appColors.primaryTeal} 100%)`,
                                                    borderRadius: "12px",
                                                    textTransform: "none",
                                                    fontWeight: 600,

                                                    "&:hover": {
                                                        background: `linear-gradient(135deg, ${appColors.primaryDark} 0%, ${appColors.primary} 100%)`,

                                                        transform: "translateY(-1px)",
                                                    },
                                                    "&.Mui-disabled": {
                                                        background: appColors.gray500,

                                                    },
                                                    transition: "all 0.3s ease"
                                                }}
                                            >
                                                {t("save")}
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            <Button
                                                variant="contained"
                                                onClick={handleNext}
                                                disabled={step === specialtyStepNumber && branchQuestionsMissing}
                                                sx={{
                                                    background: `linear-gradient(135deg, ${appColors.primary} 0%, ${appColors.primaryTeal} 100%)`,
                                                    color: "white",
                                                    minWidth: { xs: 0, sm: 120 },
                                                    flex: { xs: 1, sm: "none" },
                                                    borderRadius: "12px",
                                                    textTransform: "none",
                                                    fontWeight: 600,

                                                    "&:hover": {
                                                        background: `linear-gradient(135deg, ${appColors.primaryDark} 0%, ${appColors.primary} 100%)`,

                                                        transform: "translateY(-1px)",
                                                    },
                                                    "&.Mui-disabled": {
                                                        background: appColors.gray500,

                                                    },
                                                    transition: "all 0.3s ease"
                                                }}
                                            >
                                                {t("next")}
                                            </Button>
                                        </>
                                    )}
                                </Box>
                            </Box>

                            {/* Security Footer — kayıt akışında; profil düzenlemede gereksiz boşluk yaratmasın */}
                            {!fromProfile && (
                            <Box sx={{
                                mt: "auto",
                                px: { xs: 2, sm: 3, md: 4 },
                                py: 1.5,
                                background: appColors.bgMuted,
                                textAlign: "center",
                                flexShrink: 0,
                            }}>
                                <Typography
                                    sx={{
                                        color: appColors.gray500,
                                        fontSize: "0.75rem",
                                        mb: 0.75,
                                    }}
                                >
                                    Güvenli bağlantı ile korunmaktadır
                                </Typography>

                                <Box sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "1rem",
                                    flexWrap: "wrap",
                                }}>
                                    <Box sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "0.25rem",
                                    }}>
                                        <Typography sx={{ fontSize: "12px", color: appColors.gray500 }}>🔒</Typography>
                                        <Typography
                                            sx={{
                                                color: appColors.gray500,
                                                fontWeight: 600,
                                                fontSize: "0.75rem",
                                            }}
                                        >
                                            SSL Güvenlik
                                        </Typography>
                                    </Box>

                                    <Typography sx={{ color: appColors.gray500, fontSize: "0.75rem" }}>•</Typography>

                                    <Box sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "0.25rem",
                                    }}>
                                        <Typography sx={{ fontSize: "12px", color: appColors.gray500 }}>🔒</Typography>
                                        <Typography
                                            sx={{
                                                color: appColors.gray500,
                                                fontWeight: 600,
                                                fontSize: "0.75rem",
                                            }}
                                        >
                                            256-bit Şifreleme
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                            )}
                        </Box>
                    </Fade>
                </Container>


                <ExitConfirmationDialog
                    open={exitDialogOpen}
                    onClose={cancelExit}
                    onConfirm={confirmExit}
                    isSaveMode={fromProfile}
                />
            </Box>
        </>
    );
};

export default DoctorInfoStepper;
